import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { CountryCode } from "../api/Representation";
import { Col, Input, Row } from "antd";
import { nonEmpty } from "../../../swzutils";
import MapLoader from "../routes/map/MapLoader";
import { isStringNotBlank } from "../../../swzutils/TypeUtils";
const GooglePlacesAutoComplete = ({ value, onChange, placeHolder = "Address", autoFocus = false, showUnitOrFlat = true }) => {
    const autoCompleteRef = useRef(null);
    const unitOrFlatInputRef = useRef(null);
    const addressTextInputRef = useRef(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [unitOrFlat, setUnitOrFlat] = useState("");
    const [addressFormatted, setAddressFormatted] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [addressRequest, setAddressRequest] = useState(value);
    useEffect(() => {
        if (value) {
            const country = value.countryCode === "NZ" ? "New Zealand" : "Australia";
            setUnitOrFlat(value.unitOrFlat);
            setAddressFormatted(nonEmpty `${value.street}, ${value.suburb} ${value.state}, ${country}`);
        }
    }, [value]);
    useEffect(() => {
        if (mapLoaded) {
            installAutoComplete();
        }
    }, [mapLoaded]);
    useEffect(() => {
        if (onChange && addressRequest) {
            onChange(addressRequest, companyName);
        }
    }, [addressRequest, companyName]);
    function onUnitOrFlatChanged(e) {
        setUnitOrFlat(e.target.value);
        if (addressRequest) {
            const updatedAddressRequest = {
                ...addressRequest,
                unitOrFlat: e.target.value
            };
            setAddressRequest(updatedAddressRequest);
        }
    }
    function onFormattedAddressChanged(e) {
        setAddressFormatted(e.target.value);
    }
    const installAutoComplete = () => {
        if (!addressTextInputRef || !addressTextInputRef.current) {
            // React will call the callback with null when the component is unmounted
            console.error("Google Place AutoComplete was not installed because the input ref is not defined!");
            return;
        }
        if (autoFocus) {
            addressTextInputRef.current.focus();
        }
        //let domNode = ReactDOM.findDOMNode(addressTextInputRef.current);
        // By default, when a user selects a place, autocomplete returns all of the available
        // data fields for the selected place, and you will be billed accordingly.
        // Use Autocomplete.setFields() to specify which place data fields to return.
        const autocompleteOptions = {
            fields: ['name', 'types', 'address_components', 'geometry', 'formatted_address'],
            componentRestrictions: { country: 'au' }
        };
        autoCompleteRef.current = new google.maps.places.Autocomplete(addressTextInputRef.current, autocompleteOptions);
        autoCompleteRef.current.setFields(['name', 'types', 'address_components', 'geometry', 'formatted_address']);
        autoCompleteRef.current.addListener('place_changed', () => {
            const place = autoCompleteRef.current.getPlace();
            if (!place?.address_components?.[Symbol.iterator]) {
                return;
            }
            const isBusinessAddress = place.types && (place.types.includes("store") || place.types.includes("establishment"));
            const companyName = place.name && isBusinessAddress ? place.name : null;
            const addressText = companyName ? nonEmpty `${companyName} - ${place.formatted_address}`.trim() : place.formatted_address;
            if (addressText) {
                setAddressFormatted(addressText);
            }
            setAddressRequest(buildAddressRequestFromGooglePlace(place));
            setCompanyName(companyName);
        });
    };
    function buildAddressRequestFromGooglePlace(googlePlace) {
        const address = googlePlace.address_components;
        const intersection = getAddressComponentShortName(address, "intersection");
        const streetAddress = getAddressComponentShortName(address, "street_number") + " " + getAddressComponentShortName(address, "route");
        return {
            "unitOrFlat": getAddressComponentShortName(address, "subpremise"),
            "street": isStringNotBlank(intersection) ? "Intersection of " + intersection : streetAddress.trim(),
            "suburb": getAddressComponentShortName(address, "locality"),
            "state": getAddressComponentShortName(address, "administrative_area_level_1"),
            "postcode": getAddressComponentShortName(address, "postal_code"),
            "countryCode": getAddressComponentShortName(address, "country"),
            "latitude": googlePlace.geometry.location.lat(),
            "longitude": googlePlace.geometry.location.lng()
        };
    }
    function getAddressComponentShortName(addressComponents, typeName) {
        for (let addrComponent of addressComponents) {
            if (addrComponent.types) {
                if (addrComponent.types.includes(typeName)) {
                    return addrComponent.short_name;
                }
            }
        }
        return "";
    }
    return (_jsxs(Row, { gutter: 5, children: [showUnitOrFlat &&
                _jsx(Col, { sm: 24, md: 8, xl: 4, children: _jsx(Input, { type: "text", value: unitOrFlat || "", ref: unitOrFlatInputRef, className: "ant-input ant-input-lg", placeholder: "Unit, Shop, Level", onChange: onUnitOrFlatChanged }) }), _jsx(Col, { sm: 24, md: showUnitOrFlat ? 16 : 24, xl: showUnitOrFlat ? 20 : 24, children: _jsx("input", { type: "text", className: "swz-input-lg", placeholder: placeHolder, onChange: onFormattedAddressChanged, ref: addressTextInputRef, value: addressFormatted }) }), _jsx("div", { style: { display: "none" }, children: _jsx(MapLoader, { onMapLoaded: () => setMapLoaded(true) }) }), " "] }));
};
export default GooglePlacesAutoComplete;
